import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, Plus, Printer, Save, Trash } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS221Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list');
  const [checkouts, setCheckouts] = useState([]);
  const [editingCheckout, setEditingCheckout] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    exercise_name: '',
    date_time: '',
    demob_number: '',
    unit_personnel_released: '',
    transportation_type: '',
    actual_release_datetime: '',
    manifest_complete: '',
    // Destination checkboxes and fields
    dest_hq: false,
    dest_hq_name: '',
    dest_hq_date: '',
    dest_agency: false,
    dest_agency_name: '',
    dest_agency_date: '',
    dest_region: false,
    dest_region_name: '',
    dest_region_date: '',
    dest_area: false,
    dest_area_name: '',
    dest_area_date: '',
    dest_dispatch: false,
    dest_dispatch_name: '',
    dest_dispatch_date: '',
    unit_leader_performance: '',
    // Unit/Personnel checkboxes
    supply_unit: false,
    supply_unit_remarks: '',
    communication_unit: false,
    communication_unit_remarks: '',
    facilities_unit: false,
    facilities_unit_remarks: '',
    ground_support_unit: false,
    ground_support_unit_remarks: '',
    documentation_unit: false,
    documentation_unit_remarks: '',
    time_unit: false,
    time_unit_remarks: '',
    other_1: false,
    other_1_remarks: '',
    other_2: false,
    other_2_remarks: '',
    general_remarks: ''
  });

  // Fetch checkouts from backend
  const fetchCheckouts = async () => {
    if (!currentExercise?.id) return;
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/ics-221?exercise_id=${currentExercise.id}`, { headers });
      setCheckouts(response.data);
    } catch (error) {
      console.error('Error fetching ICS 221 checkouts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckouts();
  }, [currentExercise?.id]);

  // Fetch participants where involved_in_exercise is true AND assigned to this exercise
  useEffect(() => {
    const fetchParticipants = async () => {
      if (!currentExercise?.id) return;
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/participants`, { headers });
        // Filter participants who are involved in exercise AND assigned to this specific exercise
        const exerciseParticipants = response.data.filter(p => 
          p.involvedInExercise && 
          (p.assigned_exercise === currentExercise?.id || p.assigned_exercise === currentExercise?.exercise_name)
        );
        setParticipants(exerciseParticipants);
      } catch (error) {
        console.error('Error fetching participants:', error);
      }
    };
    fetchParticipants();
  }, [currentExercise?.id]);

  // Set exercise name when currentExercise changes
  useEffect(() => {
    if (currentExercise?.exercise_name) {
      setFormData(prev => ({
        ...prev,
        exercise_name: currentExercise.exercise_name
      }));
    }
  }, [currentExercise]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update transportation type when participant is selected
    if (field === 'unit_personnel_released') {
      const selectedParticipant = participants.find(p => p.id === value);
      if (selectedParticipant) {
        setFormData(prev => ({
          ...prev,
          transportation_type: selectedParticipant.methodOfTravel || ''
        }));
      }
    }
  };

  const handleSave = async () => {
    if (!currentExercise?.id) {
      console.error('No exercise selected');
      return;
    }

    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      const checkoutData = {
        exercise_id: currentExercise.id,
        ...formData
      };

      if (editingCheckout) {
        // Update existing entry
        await axios.put(`${API}/ics-221/${editingCheckout.id}`, checkoutData, { headers });
      } else {
        // Create new entry
        await axios.post(`${API}/ics-221`, checkoutData, { headers });
      }

      await fetchCheckouts();
      setView('list');
      setEditingCheckout(null);
      resetForm();
    } catch (error) {
      console.error('Error saving ICS 221 checkout:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (checkout) => {
    setEditingCheckout(checkout);
    setFormData({
      exercise_name: checkout.exercise_name || '',
      date_time: checkout.date_time || '',
      demob_number: checkout.demob_number || '',
      unit_personnel_released: checkout.unit_personnel_released || '',
      transportation_type: checkout.transportation_type || '',
      actual_release_datetime: checkout.actual_release_datetime || '',
      manifest_complete: checkout.manifest_complete || '',
      dest_hq: checkout.dest_hq || false,
      dest_hq_name: checkout.dest_hq_name || '',
      dest_hq_date: checkout.dest_hq_date || '',
      dest_agency: checkout.dest_agency || false,
      dest_agency_name: checkout.dest_agency_name || '',
      dest_agency_date: checkout.dest_agency_date || '',
      dest_region: checkout.dest_region || false,
      dest_region_name: checkout.dest_region_name || '',
      dest_region_date: checkout.dest_region_date || '',
      dest_area: checkout.dest_area || false,
      dest_area_name: checkout.dest_area_name || '',
      dest_area_date: checkout.dest_area_date || '',
      dest_dispatch: checkout.dest_dispatch || false,
      dest_dispatch_name: checkout.dest_dispatch_name || '',
      dest_dispatch_date: checkout.dest_dispatch_date || '',
      unit_leader_performance: checkout.unit_leader_performance || '',
      supply_unit: checkout.supply_unit || false,
      supply_unit_remarks: checkout.supply_unit_remarks || '',
      communication_unit: checkout.communication_unit || false,
      communication_unit_remarks: checkout.communication_unit_remarks || '',
      facilities_unit: checkout.facilities_unit || false,
      facilities_unit_remarks: checkout.facilities_unit_remarks || '',
      ground_support_unit: checkout.ground_support_unit || false,
      ground_support_unit_remarks: checkout.ground_support_unit_remarks || '',
      documentation_unit: checkout.documentation_unit || false,
      documentation_unit_remarks: checkout.documentation_unit_remarks || '',
      time_unit: checkout.time_unit || false,
      time_unit_remarks: checkout.time_unit_remarks || '',
      other_1: checkout.other_1 || false,
      other_1_remarks: checkout.other_1_remarks || '',
      other_2: checkout.other_2 || false,
      other_2_remarks: checkout.other_2_remarks || '',
      general_remarks: checkout.general_remarks || ''
    });
    setView('add');
  };

  const handleDelete = async (checkoutId) => {
    if (!window.confirm('Are you sure you want to delete this demobilization checkout?')) return;
    
    setLoading(true);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/ics-221/${checkoutId}`, { headers });
      await fetchCheckouts();
    } catch (error) {
      console.error('Error deleting ICS 221 checkout:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      exercise_name: currentExercise?.exercise_name || '',
      date_time: '',
      demob_number: '',
      unit_personnel_released: '',
      transportation_type: '',
      actual_release_datetime: '',
      manifest_complete: '',
      dest_hq: false,
      dest_hq_name: '',
      dest_hq_date: '',
      dest_agency: false,
      dest_agency_name: '',
      dest_agency_date: '',
      dest_region: false,
      dest_region_name: '',
      dest_region_date: '',
      dest_area: false,
      dest_area_name: '',
      dest_area_date: '',
      dest_dispatch: false,
      dest_dispatch_name: '',
      dest_dispatch_date: '',
      unit_leader_performance: '',
      supply_unit: false,
      supply_unit_remarks: '',
      communication_unit: false,
      communication_unit_remarks: '',
      facilities_unit: false,
      facilities_unit_remarks: '',
      ground_support_unit: false,
      ground_support_unit_remarks: '',
      documentation_unit: false,
      documentation_unit_remarks: '',
      time_unit: false,
      time_unit_remarks: '',
      other_1: false,
      other_1_remarks: '',
      other_2: false,
      other_2_remarks: '',
      general_remarks: ''
    });
  };

  const printICS221 = (checkout) => {
    const selectedParticipant = participants.find(p => p.id === checkout.unit_personnel_released);
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>ICS 221 - Demobilization Checkout</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; }
            .header { text-align: center; border-bottom: 2px solid #333; margin-bottom: 15px; padding-bottom: 10px; }
            .section { margin-bottom: 15px; padding: 10px; border: 1px solid #333; }
            .section-title { font-weight: bold; font-size: 12px; margin-bottom: 8px; background-color: #f0f0f0; padding: 5px; }
            .field-row { display: flex; gap: 15px; margin-bottom: 8px; }
            .field { flex: 1; }
            .field-label { font-weight: bold; font-size: 10px; margin-bottom: 3px; }
            .field-value { border-bottom: 1px solid #333; min-height: 18px; padding: 2px; }
            .checkbox-group { display: grid; grid-template-columns: 1fr 2fr; gap: 10px; margin-bottom: 5px; }
            .checkbox-item { display: flex; align-items: center; gap: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #333; padding: 6px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .footer { position: fixed; bottom: 20px; left: 20px; right: 20px; text-align: center; font-size: 9px; border-top: 1px solid #333; padding-top: 10px; }
            @media print {
              @page { margin: 15px; }
              body { margin: 0; padding: 15px; }
              .footer { position: fixed; bottom: 15px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 221 - Demobilization Checkout</h1>
          </div>
          
          <div class="section">
            <div class="field-row">
              <div class="field">
                <div class="field-label">Exercise/Incident Name:</div>
                <div class="field-value">${checkout.exercise_name || 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">Date/Time:</div>
                <div class="field-value">${checkout.date_time ? new Date(checkout.date_time).toLocaleString() : 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">Demob. Number:</div>
                <div class="field-value">${checkout.demob_number || 'N/A'}</div>
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <div class="field-label">Unit Personnel Released:</div>
                <div class="field-value">${selectedParticipant ? selectedParticipant.name : 'N/A'}</div>
              </div>
            </div>
            <div class="field-row">
              <div class="field">
                <div class="field-label">Transportation Type:</div>
                <div class="field-value">${checkout.transportation_type || 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">Actual Release Date/Time:</div>
                <div class="field-value">${checkout.actual_release_datetime ? new Date(checkout.actual_release_datetime).toLocaleString() : 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">Manifest Complete:</div>
                <div class="field-value">${checkout.manifest_complete || 'N/A'}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Destination</div>
            <table>
              <tr>
                <th>Type</th>
                <th>Selected</th>
                <th>Name</th>
                <th>Date</th>
              </tr>
              <tr>
                <td>HQ</td>
                <td>${checkout.dest_hq ? '✓' : ''}</td>
                <td>${checkout.dest_hq_name || ''}</td>
                <td>${checkout.dest_hq_date || ''}</td>
              </tr>
              <tr>
                <td>Agency</td>
                <td>${checkout.dest_agency ? '✓' : ''}</td>
                <td>${checkout.dest_agency_name || ''}</td>
                <td>${checkout.dest_agency_date || ''}</td>
              </tr>
              <tr>
                <td>Region</td>
                <td>${checkout.dest_region ? '✓' : ''}</td>
                <td>${checkout.dest_region_name || ''}</td>
                <td>${checkout.dest_region_date || ''}</td>
              </tr>
              <tr>
                <td>Area</td>
                <td>${checkout.dest_area ? '✓' : ''}</td>
                <td>${checkout.dest_area_name || ''}</td>
                <td>${checkout.dest_area_date || ''}</td>
              </tr>
              <tr>
                <td>Dispatch</td>
                <td>${checkout.dest_dispatch ? '✓' : ''}</td>
                <td>${checkout.dest_dispatch_name || ''}</td>
                <td>${checkout.dest_dispatch_date || ''}</td>
              </tr>
            </table>
            <div class="field-row" style="margin-top: 10px;">
              <div class="field">
                <div class="field-label">Unit Leader Responsible for Collecting Performance Rating:</div>
                <div class="field-value">${checkout.unit_leader_performance || ''}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Unit/Personnel</div>
            <table>
              <tr>
                <th>Section</th>
                <th>Unit</th>
                <th>Selected</th>
                <th>Remarks</th>
              </tr>
              <tr>
                <td rowspan="4">Logistics</td>
                <td>Supply Unit</td>
                <td>${checkout.supply_unit ? '✓' : ''}</td>
                <td>${checkout.supply_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td>Communication Unit</td>
                <td>${checkout.communication_unit ? '✓' : ''}</td>
                <td>${checkout.communication_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td>Facilities Unit</td>
                <td>${checkout.facilities_unit ? '✓' : ''}</td>
                <td>${checkout.facilities_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td>Ground Support Unit</td>
                <td>${checkout.ground_support_unit ? '✓' : ''}</td>
                <td>${checkout.ground_support_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td>Planning</td>
                <td>Documentation Unit</td>
                <td>${checkout.documentation_unit ? '✓' : ''}</td>
                <td>${checkout.documentation_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td>Finance</td>
                <td>Time Unit</td>
                <td>${checkout.time_unit ? '✓' : ''}</td>
                <td>${checkout.time_unit_remarks || ''}</td>
              </tr>
              <tr>
                <td rowspan="2">Other</td>
                <td>Other 1</td>
                <td>${checkout.other_1 ? '✓' : ''}</td>
                <td>${checkout.other_1_remarks || ''}</td>
              </tr>
              <tr>
                <td>Other 2</td>
                <td>${checkout.other_2 ? '✓' : ''}</td>
                <td>${checkout.other_2_remarks || ''}</td>
              </tr>
            </table>
          </div>

          <div class="section">
            <div class="section-title">General Remarks</div>
            <div>${checkout.general_remarks || 'None'}</div>
          </div>

          <div class="footer">
            ICS 221 - Demobilization Checkout | Generated on ${new Date().toLocaleString()} | EXRSIM Emergency Management Platform
          </div>
        </body>
      </html>
    `;
    
    writeSanitizedHTML(printWindow.document, printContent);
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // List View
  if (view === 'list') {
    return (
      <div className="p-6" data-testid="ics-221-list-view">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>ICS 221 - Demobilization Checkout</h2>
            <p className={`${theme.colors.textMuted} mt-1`}>Process demobilization checkouts for personnel and resources</p>
          </div>
          <Button 
            onClick={() => {
              resetForm();
              setView('add');
            }}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="new-ics-221-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New ICS 221
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
            <p className={`mt-2 ${theme.colors.textMuted}`}>Loading checkouts...</p>
          </div>
        ) : checkouts.length === 0 ? (
          <div className={`text-center py-12 ${theme.colors.card} rounded-lg border ${theme.colors.border}`}>
            <ArrowLeft className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <p className={`${theme.colors.textMuted} mb-4`}>No demobilization checkouts yet</p>
            <Button onClick={() => {
              resetForm();
              setView('add');
            }} className="bg-orange-600 hover:bg-orange-700" data-testid="create-first-ics-221-btn">
              Create First ICS 221
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {checkouts.map((checkout) => {
              const participant = participants.find(p => p.id === checkout.unit_personnel_released);
              return (
                <div key={checkout.id} className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-4`} data-testid={`ics-221-entry-${checkout.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className={`font-semibold ${theme.colors.textPrimary}`}>{checkout.exercise_name || 'Untitled'}</h3>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Demob #: {checkout.demob_number || 'N/A'} | Personnel: {participant?.name || participant?.firstName && participant?.lastName ? `${participant.firstName} ${participant.lastName}` : 'N/A'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Date/Time: {checkout.date_time ? new Date(checkout.date_time).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printICS221(checkout)}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                        data-testid={`print-ics-221-${checkout.id}`}
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(checkout)}
                        className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                        data-testid={`edit-ics-221-${checkout.id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(checkout.id)}
                        className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                        data-testid={`delete-ics-221-${checkout.id}`}
                      >
                        <Trash className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Add/Edit View
  return (
    <div className="p-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>
            {editingCheckout ? 'Edit' : 'Create'} ICS 221 - Demobilization Checkout
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingCheckout(null);
              resetForm();
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          {editingCheckout && (
            <Button
              variant="outline"
              onClick={() => printICS221(formData)}
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print ICS 221
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Header Information Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <h3 className="text-lg font-semibold text-orange-500 mb-4">Header Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-white">Unit Personnel Released</Label>
              <Select value={formData.unit_personnel_released} onValueChange={(value) => handleInputChange('unit_personnel_released', value)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white">
                  <SelectValue placeholder="Select participant..." />
                </SelectTrigger>
                <SelectContent>
                  {participants.map((participant) => (
                    <SelectItem key={participant.id} value={participant.id}>
                      {`${participant.lastName || participant.lastname || ''}, ${participant.firstName || participant.firstname || ''} - ${participant.position || ''}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Date/Time</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={formData.date_time ? formData.date_time.split('T')[0] : ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = formData.date_time ? formData.date_time.split('T')[1] : '00:00';
                    handleInputChange('date_time', `${date}T${time}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <Input
                  type="text"
                  placeholder="HH:MM"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  maxLength="5"
                  value={formData.date_time ? formData.date_time.split('T')[1] : ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9:]/g, '');
                    if (value.length === 2 && !value.includes(':')) {
                      value = value + ':';
                    }
                    if (value.length <= 5) {
                      const date = formData.date_time ? formData.date_time.split('T')[0] : '';
                      handleInputChange('date_time', `${date}T${value}`);
                    }
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-white">Demob. Number</Label>
              <Input
                value={formData.demob_number}
                onChange={(e) => handleInputChange('demob_number', e.target.value)}
                placeholder="Demobilization number"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-white">Exercise/Incident Name</Label>
            <Input
              value={formData.exercise_name}
              onChange={(e) => handleInputChange('exercise_name', e.target.value)}
              className="bg-gray-800 border-gray-700 text-white"
              readOnly
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-white">Transportation Type</Label>
              <Select value={formData.transportation_type} onValueChange={(value) => handleInputChange('transportation_type', value)}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="transportation-type-select">
                  <SelectValue placeholder="Select transportation method..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="Vehicle" className="text-white focus:bg-gray-700">Vehicle</SelectItem>
                  <SelectItem value="Airlines" className="text-white focus:bg-gray-700">Airlines</SelectItem>
                  <SelectItem value="Bus" className="text-white focus:bg-gray-700">Bus</SelectItem>
                  <SelectItem value="Rail" className="text-white focus:bg-gray-700">Rail</SelectItem>
                  <SelectItem value="Helicopter" className="text-white focus:bg-gray-700">Helicopter</SelectItem>
                  <SelectItem value="Taxi /UBER" className="text-white focus:bg-gray-700">Taxi /UBER</SelectItem>
                  <SelectItem value="Other" className="text-white focus:bg-gray-700">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Actual Release Date/Time</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={formData.actual_release_datetime ? formData.actual_release_datetime.split('T')[0] : ''}
                  onChange={(e) => {
                    const date = e.target.value;
                    const time = formData.actual_release_datetime ? formData.actual_release_datetime.split('T')[1] : '00:00';
                    handleInputChange('actual_release_datetime', `${date}T${time}`);
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                <Input
                  type="text"
                  placeholder="HH:MM"
                  pattern="[0-2][0-9]:[0-5][0-9]"
                  maxLength="5"
                  value={formData.actual_release_datetime ? formData.actual_release_datetime.split('T')[1] : ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^0-9:]/g, '');
                    if (value.length === 2 && !value.includes(':')) {
                      value = value + ':';
                    }
                    if (value.length <= 5) {
                      const date = formData.actual_release_datetime ? formData.actual_release_datetime.split('T')[0] : '';
                      handleInputChange('actual_release_datetime', `${date}T${value}`);
                    }
                  }}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-white">Manifest Complete</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-white">
                  <input
                    type="radio"
                    name="manifest_complete"
                    value="Yes"
                    checked={formData.manifest_complete === 'Yes'}
                    onChange={(e) => handleInputChange('manifest_complete', e.target.value)}
                    className="form-radio"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-white">
                  <input
                    type="radio"
                    name="manifest_complete"
                    value="No"
                    checked={formData.manifest_complete === 'No'}
                    onChange={(e) => handleInputChange('manifest_complete', e.target.value)}
                    className="form-radio"
                  />
                  No
                </label>
              </div>
            </div>
          </div>

          {/* Destination Section */}
          <div className="mt-6">
            <h4 className="text-md font-semibold text-orange-500 mb-3">Destination</h4>
            <div className="space-y-3">
              {[
                { key: 'hq', label: 'HQ' },
                { key: 'agency', label: 'Agency' },
                { key: 'region', label: 'Region' },
                { key: 'area', label: 'Area' },
                { key: 'dispatch', label: 'Dispatch' }
              ].map((dest) => (
                <div key={dest.key} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                  <label className="flex items-center gap-2 text-white">
                    <Checkbox
                      checked={formData[`dest_${dest.key}`]}
                      onCheckedChange={(checked) => handleInputChange(`dest_${dest.key}`, checked)}
                    />
                    {dest.label}
                  </label>
                  <div>
                    <Label className="text-white text-xs">Name</Label>
                    <Input
                      value={formData[`dest_${dest.key}_name`]}
                      onChange={(e) => handleInputChange(`dest_${dest.key}_name`, e.target.value)}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-xs">Date</Label>
                    <div className="grid grid-cols-2 gap-1">
                      <Input
                        type="date"
                        value={formData[`dest_${dest.key}_date`] ? formData[`dest_${dest.key}_date`].split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData[`dest_${dest.key}_date`] ? formData[`dest_${dest.key}_date`].split('T')[1] : '00:00';
                          handleInputChange(`dest_${dest.key}_date`, `${date}T${time}`);
                        }}
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        value={formData[`dest_${dest.key}_date`] ? formData[`dest_${dest.key}_date`].split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = formData[`dest_${dest.key}_date`] ? formData[`dest_${dest.key}_date`].split('T')[0] : '';
                            handleInputChange(`dest_${dest.key}_date`, `${date}T${value}`);
                          }
                        }}
                        className="bg-gray-800 border-gray-700 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4">
              <Label className="text-white">Unit Leader Responsible for Collecting Performance Rating</Label>
              <Input
                value={formData.unit_leader_performance}
                onChange={(e) => handleInputChange('unit_leader_performance', e.target.value)}
                placeholder="Unit leader name"
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Unit/Personnel Card */}
        <div className={`${theme.colors.card} rounded-lg border ${theme.colors.border} p-6`}>
          <h3 className="text-lg font-semibold text-orange-500 mb-4">Unit/Personnel</h3>
          
          {/* Logistics Section */}
          <div className="mb-4">
            <h4 className="text-md font-semibold text-orange-500 mb-3">Logistics Section</h4>
            <div className="space-y-3">
              {[
                { key: 'supply_unit', label: 'Supply Unit' },
                { key: 'communication_unit', label: 'Communication Unit' },
                { key: 'facilities_unit', label: 'Facilities Unit' },
                { key: 'ground_support_unit', label: 'Ground Support Unit' }
              ].map((unit) => (
                <div key={unit.key} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                  <label className="flex items-center gap-2 text-white">
                    <Checkbox
                      checked={formData[unit.key]}
                      onCheckedChange={(checked) => handleInputChange(unit.key, checked)}
                    />
                    {unit.label}
                  </label>
                  <div className="md:col-span-3">
                    <Input
                      value={formData[`${unit.key}_remarks`]}
                      onChange={(e) => handleInputChange(`${unit.key}_remarks`, e.target.value)}
                      placeholder="Remarks"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Planning Section */}
          <div className="mb-4">
            <h4 className="text-md font-semibold text-orange-500 mb-3">Planning Section</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
              <label className="flex items-center gap-2 text-white">
                <Checkbox
                  checked={formData.documentation_unit}
                  onCheckedChange={(checked) => handleInputChange('documentation_unit', checked)}
                />
                Documentation Unit
              </label>
              <div className="md:col-span-3">
                <Input
                  value={formData.documentation_unit_remarks}
                  onChange={(e) => handleInputChange('documentation_unit_remarks', e.target.value)}
                  placeholder="Remarks"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Finance Section */}
          <div className="mb-4">
            <h4 className="text-md font-semibold text-orange-500 mb-3">Finance Section</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
              <label className="flex items-center gap-2 text-white">
                <Checkbox
                  checked={formData.time_unit}
                  onCheckedChange={(checked) => handleInputChange('time_unit', checked)}
                />
                Time Unit
              </label>
              <div className="md:col-span-3">
                <Input
                  value={formData.time_unit_remarks}
                  onChange={(e) => handleInputChange('time_unit_remarks', e.target.value)}
                  placeholder="Remarks"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Other Section */}
          <div className="mb-4">
            <h4 className="text-md font-semibold text-orange-500 mb-3">Other</h4>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                <label className="flex items-center gap-2 text-white">
                  <Checkbox
                    checked={formData.other_1}
                    onCheckedChange={(checked) => handleInputChange('other_1', checked)}
                  />
                  Other
                </label>
                <div className="md:col-span-3">
                  <Input
                    value={formData.other_1_remarks}
                    onChange={(e) => handleInputChange('other_1_remarks', e.target.value)}
                    placeholder="Remarks"
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                <label className="flex items-center gap-2 text-white">
                  <Checkbox
                    checked={formData.other_2}
                    onCheckedChange={(checked) => handleInputChange('other_2', checked)}
                  />
                  Other
                </label>
                <div className="md:col-span-3">
                  <Input
                    value={formData.other_2_remarks}
                    onChange={(e) => handleInputChange('other_2_remarks', e.target.value)}
                    placeholder="Remarks"
                    className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* General Remarks */}
          <div>
            <Label className="text-white">Remarks</Label>
            <Textarea
              value={formData.general_remarks}
              onChange={(e) => handleInputChange('general_remarks', e.target.value)}
              placeholder="General remarks"
              className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
              rows={4}
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingCheckout(null);
              resetForm();
            }}
            data-testid="cancel-ics-221-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-orange-600 hover:bg-orange-700"
            disabled={loading}
            data-testid="save-ics-221-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : (editingCheckout ? 'Update' : 'Save')} ICS 221
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 224 - Crew Performance Rating Form

export default ICS221Form;
